/**
 * Фокус с клавиатуры всегда виден целиком.
 *
 * Браузер, ставя фокус, докручивает к элементу, только если тот спрятан
 * полностью: карточка, выглядывающая из-за края ряда или из-под нижнего меню
 * телефона, считается «видной», и рамка фокуса остаётся наполовину за краем.
 * Здесь после каждого перехода фокуса с клавиатуры проверяем, помещается ли
 * элемент в свой ряд и в окно за вычетом `scroll-padding` (под ним — нижнее
 * меню и липкие шапки), и если нет — докручиваем.
 *
 * Мышь и палец не трогаем (`:focus-visible`): нажатие на выглядывающую
 * карточку не должно дёргать ряд под рукой. Элементы внутри фиксированных
 * слоёв (меню, плеер) прокрутке не подчиняются — их пропускаем; в липких
 * (шапка админки) докручиваем только их собственную полосу вбок.
 */

/** Запас на округления и на рамку фокуса у самого края. */
const SLACK = 2;

function px(value: string): number {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : 0;
}

/** В каком слое элемент: фиксированный не прокручивается вовсе, липкий — только вбок. */
function layerOf(element: HTMLElement): 'fixed' | 'sticky' | 'page' {
  let layer: 'sticky' | 'page' = 'page';
  for (let node: HTMLElement | null = element; node !== null; node = node.parentElement) {
    const position = getComputedStyle(node).position;
    if (position === 'fixed') return 'fixed';
    if (position === 'sticky') layer = 'sticky';
  }
  return layer;
}

/** Ближайший предок, прокручиваемый вбок (ряд карточек, полоса капсул). */
function horizontalScroller(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node !== null && node !== document.body; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowX;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollWidth > node.clientWidth + 1) return node;
  }
  return null;
}

/**
 * Сдвиг окна по вертикали, при котором элемент встаёт в видимую полосу
 * (0 — уже стоит).
 */
function verticalShift(element: HTMLElement, box: DOMRect): number {
  // Поля прокрутки окна (над нижним меню) плюс собственные поля элемента
  // (`scroll-margin` под липкой шапкой админки) — та же полоса, которую
  // соблюдает и сама прокрутка.
  const root = getComputedStyle(document.documentElement);
  const own = getComputedStyle(element);
  const top = px(root.scrollPaddingTop) + px(own.scrollMarginTop);
  const bottom = window.innerHeight - px(root.scrollPaddingBottom) - px(own.scrollMarginBottom);
  if (box.top < top - SLACK) return box.top - top;
  // Выше видимой полосы, чем она сама, элемент целиком не покажешь —
  // тогда хватит того, что виден его верх.
  if (box.height > bottom - top) return box.top > bottom ? box.top - top : 0;
  if (box.bottom > bottom + SLACK) return box.bottom - bottom;
  return 0;
}

/**
 * Новая позиция ряда, при которой элемент виден целиком, или `null`.
 *
 * Ряды прилипают к началу карточек (`scroll-snap`), и «ближайшая» прокрутка
 * на несколько точек тут же отскакивала к прежней карточке — фокус так и
 * оставался за краем. Поэтому ряд встаёт сразу на ту точку прилипания,
 * с которой элемент виден: влево — на сам элемент, вправо — на ближайшее
 * начало карточки, после которого его правый край в кадре.
 */
function horizontalTarget(scroller: HTMLElement, box: DOMRect): number | null {
  const frame = scroller.getBoundingClientRect();
  const style = getComputedStyle(scroller);
  const left = frame.left + px(style.scrollPaddingLeft);
  const right = frame.right - px(style.scrollPaddingRight);
  if (box.width > right - left) return null;
  if (box.left < left - SLACK) return Math.max(0, scroller.scrollLeft + box.left - left);
  if (box.right <= right + SLACK) return null;
  const needed = scroller.scrollLeft + box.right - right;
  let target: number | null = null;
  for (const child of Array.from(scroller.children)) {
    const position = scroller.scrollLeft + child.getBoundingClientRect().left - left;
    if (position >= needed - SLACK && (target === null || position < target)) target = position;
  }
  return Math.min(target ?? needed, scroller.scrollWidth - scroller.clientWidth);
}

function reveal(element: HTMLElement, vertical: boolean): void {
  const scroller = horizontalScroller(element);
  if (scroller !== null) {
    const target = horizontalTarget(scroller, element.getBoundingClientRect());
    if (target !== null) scroller.scrollTo({ left: target });
  }
  if (vertical) {
    const shift = verticalShift(element, element.getBoundingClientRect());
    if (shift !== 0) window.scrollBy({ top: shift });
  }
}

/** Последний ввод был с клавиатуры (или пульта), а не мышью или пальцем. */
let keyboardInput = false;

/**
 * Пришёл ли зритель с клавиатуры: экран, открывающийся «на нужном месте»
 * (список серий), ставит туда фокус только ему — мышь и палец фокус не
 * видят, а рамка на чужой карточке им только мешает.
 */
export function lastInputWasKeyboard(): boolean {
  return keyboardInput;
}

export function installFocusReveal(): () => void {
  let frame = 0;
  const onKey = (): void => {
    keyboardInput = true;
  };
  const onPointer = (): void => {
    keyboardInput = false;
  };
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('pointerdown', onPointer, true);
  const onFocusIn = (event: FocusEvent): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    // Старые движки без :focus-visible бросают исключение — тогда молчим.
    let keyboard = false;
    try {
      keyboard = target.matches(':focus-visible');
    } catch {
      return;
    }
    if (!keyboard) return;
    cancelAnimationFrame(frame);
    // Кадр спустя: своя прокрутка браузера к фокусу уже отработала, и
    // проверяем то положение, которое увидит зритель.
    frame = requestAnimationFrame(() => {
      if (document.activeElement !== target || !target.isConnected) return;
      const layer = layerOf(target);
      if (layer === 'fixed') return;
      reveal(target, layer === 'page');
    });
  };
  document.addEventListener('focusin', onFocusIn);
  return () => {
    cancelAnimationFrame(frame);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('pointerdown', onPointer, true);
  };
}
